import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { BasicCommunityMember } from "@circle-react/types/CommunityMember";
import type { ContentReactionType } from "@circle-react/types/ContentReaction";
import type { PaginationHelper } from "@circle-react/types/InternalAPIHelper";
import { infiniteQueryOptions } from "react-query";

// All comment-like keys share the ["comment-like", ...] prefix, so a single
// invalidation at the list root refreshes the active filter and marks the
// remaining cached filters as stale.

const commentLikeKey = createQueryKeyFactory("comment-like");

const COMMENT_LIKES_PER_PAGE = 15;

export interface CommentLikeMember extends BasicCommunityMember {
  reaction_type?: ContentReactionType;
}

type CommentLikesPage = PaginationHelper<CommentLikeMember>;

export const commentLikeQueries = {
  list: (commentId: number, reactionType?: ContentReactionType | null) =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: commentLikeKey.key("list", commentId, reactionType ?? "all"),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<CommentLikesPage>(
          internalApi.comments.userLikes({
            id: commentId,
            params: {
              page: pageParam,
              per_page: COMMENT_LIKES_PER_PAGE,
              ...(reactionType && { reaction_type: reactionType }),
            },
          }),
        ),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    }),
};

export const commentLikeInvalidations = {
  list: (commentId: number) =>
    queryClient.invalidateQueries({
      queryKey: commentLikeKey.key("list", commentId),
    }),
};
